import React, { useMemo, useState } from 'react';
import { ListItemAvatar, ListItemText, Typography, Stack, ListItem } from '@mui/material';
import { useNavigate } from 'react-router';

import { Job, JobStatus } from 'src/@types/jobs';

import Iconify from '../../../components/iconify';
import NotificationInfoPopup from './NotificationInfoPopup';
import { fDateTimeDe } from '../../../utils/timeAndDateHelper';
import { useNotificationContent } from './notificationUtils';
import { useLocales } from '../../../locales';
import { dispatch } from '../../../redux/store';
import { apiSlice } from '../../../redux/slices/apiSlice';

type NotificationItemProps = {
  notification: Job;
};

export default function NotificationItem({ notification }: NotificationItemProps) {
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const { translate } = useLocales();
  const navigate = useNavigate();

  const renderNotificationContent = useNotificationContent();
  const notificationContent = renderNotificationContent(notification);
  const { jobInformation } = notificationContent;
  const linkHref = 'linkHref' in notificationContent ? notificationContent.linkHref : undefined;

  const duplicateCount = useMemo(() => {
    if (notification.type !== 'EXCEL_IMPORT') {
      return 0;
    }

    return (
      notification.resultPayload?.summary?.conflictCount ??
      notification.resultPayload?.conflicts?.length ??
      0
    );
  }, [notification.type, notification.resultPayload]);

  const isResolvedExcelConflict = useMemo(
    () =>
      notification.type === 'EXCEL_IMPORT' &&
      notification.resultPayload?.summary?.resolved === true,
    [notification.type, notification.resultPayload]
  );

  const shouldShowPopup = useMemo(() => {
    const popupStatuses: Partial<Record<string, JobStatus[]>> = {
      EXCEL_IMPORT: [JobStatus.ERROR, JobStatus.WARNING],
      IMPORT_EMPLOYEES_INTO_KEYCLOAK: [JobStatus.ERROR, JobStatus.PARTIALLY_FINISHED],
    };

    return popupStatuses[notification.type]?.includes(notification.status) ?? false;
  }, [notification.type, notification.status]);

  const linksToImportedStudents =
    notification.type === 'EXCEL_IMPORT' &&
    notification.status === JobStatus.FINISHED &&
    linkHref !== undefined &&
    linkHref !== '#';

  const handleClick = () => {
    if (shouldShowPopup) {
      setShowInfoPopup(true);
    } else if (linksToImportedStudents) {
      dispatch(apiSlice.util.invalidateTags(['Student']));
      navigate(linkHref);
    }
  };

  const statusConfig: Partial<
    Record<
      JobStatus,
      {
        icon: string;
        color: string;
        backgroundColor?: string;
      }
    >
  > = {
    [JobStatus.FINISHED]: {
      icon: 'mdi:check-circle',
      color: 'success.light',
    },

    [JobStatus.STARTED]: {
      icon: 'mdi:checkbox-blank-circle-outline',
      color: 'warning.light',
    },

    [JobStatus.PARTIALLY_FINISHED]: {
      icon: 'mdi:circle-outline',
      color: 'warning.main',
    },

    [JobStatus.WARNING]: {
      icon: 'mdi:account-alert-outline',
      color: 'warning.main',
    },

    [JobStatus.ERROR]: {
      icon: 'mdi:close-circle-outline',
      color: 'error.main',
    },
  };

  const currentStatus = statusConfig[notification.status] ?? statusConfig[JobStatus.ERROR]!;

  const listItemBackground = !notification.read
    ? (currentStatus.backgroundColor ?? 'action.selected')
    : currentStatus.backgroundColor;

  return (
    <>
      <ListItem
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2.5,
          mt: '1px',
          cursor: shouldShowPopup || linksToImportedStudents ? 'pointer' : 'default',

          ...(listItemBackground && {
            bgcolor: listItemBackground,
          }),
        }}
      >
        <ListItemAvatar>
          <Iconify
            icon={currentStatus.icon}
            width={24}
            height={24}
            sx={{ color: currentStatus.color }}
          />
        </ListItemAvatar>

        <ListItemText
          primary={
            <Stack direction="column" spacing={0.5}>
              <Typography variant="caption" color="text.disabled">
                {jobInformation}
              </Typography>

              {duplicateCount > 0 && (
                <Typography variant="caption" color="warning.main">
                  {`${translate('excelImport.duplicatesFound', { count: duplicateCount })}`}
                </Typography>
              )}

              {isResolvedExcelConflict && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Iconify
                    icon="mdi:check-circle-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'success.main' }}
                  />

                  <Typography variant="caption" color="success.main">
                    {`${translate('excelImport.conflictResolution.resolvedLabel')}`}
                  </Typography>
                </Stack>
              )}
            </Stack>
          }
          secondary={
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                mt: 0.5,
                typography: 'caption',
                color: 'text.disabled',
              }}
            >
              <Typography variant="caption">{fDateTimeDe(notification.started)}</Typography>

              <Typography variant="caption">-</Typography>

              <Typography variant="caption">{notification.createdByName}</Typography>
            </Stack>
          }
        />
      </ListItem>

      {showInfoPopup && (
        <NotificationInfoPopup
          notificationContent={notification}
          setShowPopup={setShowInfoPopup}
          notificationType={notification.type}
        />
      )}
    </>
  );
}
